import {
  doc,
  getDoc,
  onSnapshot,
  arrayUnion,
  arrayRemove,
  increment,
  serverTimestamp
} from 'firebase/firestore'
import { db } from './firebase'
import { format, addDays, startOfToday } from 'date-fns' // Import date-fns for date manipulation
import api from './api'

// Coleções do Firestore (mantidas para outros serviços que ainda as utilizam)
const COLLECTIONS = {
  EVENTS: 'events', // Although events are now fetched from API, keep for potential related data
  ATTENDEES: 'attendees',
  REACTIONS: 'reactions',
  TICKETS: 'tickets'
}

/**
 * Serviço de Eventos
 */
export const eventService = {
  /**
   * Buscar todos os eventos da API
   * @param {string} unitId - O ID da unidade (cidade) para filtrar eventos
   * @param {Date} startDate - Data de início para filtrar eventos (opcional)
   * @param {Date} endDate - Data de fim para filtrar eventos (opcional)
   */
  getAll: async (unitId = null, startDate = null, endDate = null, minimal = false) => {
    try {
      const params = {};

      if (unitId) {
        params.unit_id = unitId;
      }

      const today = startOfToday();
      if (!startDate) {
        startDate = today;
      }
      if (!endDate) {
        endDate = addDays(today, 7); // Default to next 7 days if no end date
      }

      params.start_date = format(startDate, 'yyyy-MM-dd');
      params.end_date = format(endDate, 'yyyy-MM-dd');
      params.sale_status = 'OPEN';
      
      if (minimal) {
        params.minimal = 'true';
      }

      const response = await api.get('/events/', params);
      return response; // api.js now extracts the 'data' field automatically
    } catch (error) {
      console.error('Erro ao buscar eventos:', error)
      throw error
    }
  },

  /**
   * Buscar perguntas extras de um evento
   */
  getQuestions: async (eventId) => {
    return api.get(`/events/${eventId}/questions`)
  },

  /**
   * Buscar evento por ID
   */
  getById: async (eventId) => {
    try {
      // Assuming individual event fetching still goes through Firestore, or needs to be updated too.
      // For now, keeping Firestore logic as per current file.
      const docRef = doc(db, COLLECTIONS.EVENTS, eventId)
      const docSnap = await getDoc(docRef)
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() }
      }
      return null
    } catch (error) {
      console.error('Erro ao buscar evento:', error)
      throw error
    }
  },

  // getFeatured is removed as per plan, logic will be handled in Home.jsx

  /**
   * Escutar mudanças em tempo real de um evento
   */
  subscribeToEvent: (eventId, callback) => {
    const docRef = doc(db, COLLECTIONS.EVENTS, eventId)
    return onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        callback({ id: doc.id, ...doc.data() })
      }
    })
  },

  /**
   * Escutar lista de participantes em tempo real
   */
  subscribeToAttendees: (eventId, callback) => {
    const q = query(
      collection(db, COLLECTIONS.ATTENDEES),
      where('eventId', '==', eventId),
      where('visible', '==', true),
      orderBy('createdAt', 'desc'),
      limit(50)
    )
    
    return onSnapshot(q, (snapshot) => {
      const attendees = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      callback(attendees)
    })
  },

  /**
   * Adicionar participante ao evento
   */
  addAttendee: async (eventId, userData, visible = true) => {
    try {
      const attendeeData = {
        eventId,
        userId: userData.id,
        name: userData.name,
        avatar: userData.avatar || null,
        visible,
        createdAt: serverTimestamp()
      }
      
      const docRef = await addDoc(collection(db, COLLECTIONS.ATTENDEES), attendeeData)
      return docRef.id
    } catch (error) {
      console.error('Erro ao adicionar participante:', error)
      throw error
    }
  },

  /**
   * Atualizar visibilidade do participante
   */
  updateAttendeeVisibility: async (attendeeId, visible) => {
    try {
      const docRef = doc(db, COLLECTIONS.ATTENDEES, attendeeId)
      await updateDoc(docRef, { visible })
    } catch (error) {
      console.error('Erro ao atualizar visibilidade:', error)
      throw error
    }
  }
}

/**
 * Serviço de Reações
 */
export const reactionService = {
  /**
   * Escutar reações em tempo real
   */
  subscribeToReactions: (eventId, callback) => {
    const docRef = doc(db, COLLECTIONS.REACTIONS, eventId)
    return onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        callback(doc.data())
      } else {
        callback({ emojis: {} })
      }
    })
  },

  /**
   * Adicionar reação (emoji)
   */
  addReaction: async (eventId, emoji, userId) => {
    try {
      const docRef = doc(db, COLLECTIONS.REACTIONS, eventId)
      const docSnap = await getDoc(docRef)
      
      if (docSnap.exists()) {
        await updateDoc(docRef, {
          [`emojis.${emoji}.count`]: increment(1),
          [`emojis.${emoji}.users`]: arrayUnion(userId),
          updatedAt: serverTimestamp()
        })
      } else {
        await addDoc(collection(db, COLLECTIONS.REACTIONS), {
          eventId,
          emojis: {
            [emoji]: {
              count: 1,
              users: [userId]
            }
          },
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
      }
    } catch (error) {
      console.error('Erro ao adicionar reação:', error)
      throw error
    }
  },

  /**
   * Remover reação
   */
  removeReaction: async (eventId, emoji, userId) => {
    try {
      const docRef = doc(db, COLLECTIONS.REACTIONS, eventId)
      await updateDoc(docRef, {
        [`emojis.${emoji}.count`]: increment(-1),
        [`emojis.${emoji}.users`]: arrayRemove(userId),
        updatedAt: serverTimestamp()
      })
    } catch (error) {
      console.error('Erro ao remover reação:', error)
      throw error
    }
  }
}

/**
 * Serviço de Ingressos (via API Backend)
 */
export const ticketService = {
  /**
   * Buscar categorias de ingressos de um evento
   */
  getTicketCategories: async (eventId) => {
    return api.get(`/events/${eventId}/tickets`)
  },

  /**
   * Iniciar compra de ingresso
   */
  startPurchase: async (eventId, ticketData) => {
    return api.post(`/events/${eventId}/purchase`, ticketData)
  },

  /**
   * Confirmar pagamento
   */
  confirmPayment: async (transactionId, paymentData) => {
    return api.post(`/sales/${transactionId}/payment`, paymentData)
  },

  /**
   * Buscar status da venda
   */
  getSaleStatus: async (transactionId) => {
    return api.get(`/sales/${transactionId}/status`)
  },

  /**
   * Buscar ingressos do usuário
   */
  getUserTickets: async () => {
    return api.get('/user/tickets')
  }
}

export default {
  eventService,
  reactionService,
  ticketService
}
